/// <reference types="vite/client" />
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from "@sentry/react";
import App from './src/App';
import EmailVerificationGate from './src/components/EmailVerificationGate';
import { SentryErrorBoundary } from './src/components/SentryErrorBoundary';
import { initializePwa } from './src/services/pwa';
import { isSentryCapturedError } from './src/services/sentryCapture';
import { redactSentryBreadcrumb, redactSentryEvent, shouldDropExpectedSentryError } from './src/services/sentryPrivacy';
import './src/index.css';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE === 'development' ? 'development' : 'production',
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    initialScope: {
      tags: {
        juris_runtime: 'browser',
      },
    },
    integrations: (integrations) => {
      const filtered = integrations.filter((integration) => ![
        'BrowserTracing',
        'Replay'
      ].includes(integration.name));
      return [
        ...filtered,
        Sentry.globalHandlersIntegration({
          onerror: true,
          onunhandledrejection: true,
        }),
        Sentry.browserTracingIntegration(),
      ];
    },
    sampleRate: 1.0,
    tracesSampleRate: 0.02,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,

    beforeBreadcrumb(breadcrumb) {
      return redactSentryBreadcrumb(breadcrumb);
    },

    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      if (shouldDropExpectedSentryError(hint.originalException)) {
        return null;
      }

      try {
        redactSentryEvent(event);
      } catch {
        return null;
      }

      if (event.tags?.source === 'manual') return event;

      if (isSentryCapturedError(hint.originalException)) {
        return null;
      }

      return event;
    },
  });
}

const rootElement = document.getElementById('root');

initializePwa();

if (!rootElement) {
  throw new Error("No se pudo encontrar el elemento root para montar la aplicacion.");
}

const root = ReactDOM.createRoot(rootElement);

root.render(
  <React.StrictMode>
    <SentryErrorBoundary>
      <EmailVerificationGate><App /></EmailVerificationGate>
    </SentryErrorBoundary>
  </React.StrictMode>
);
